import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { setBarcodeInSection } from '@/actions';
import { getAllLayers } from 'pxc-editor-core/helpers/schema';
import { getOnlyDiffProps } from '@/helpers/common';
import InsertCodeForm from './InsertCodeForm';
import { linearCodes, twoDCodes } from '@/configs/barcode-cmme';

const codesByGroup = {
  linearCodes: linearCodes,
  twoDCodes: twoDCodes
};

const codeGroups = Object.keys(codesByGroup);

const InsertCodeFormContainer = props => {
  const {
    pages,
    sectionsSelection,
    setBarcodeInSection,
    compRef,
    ...restProps
  } = props;

  const formCompRef = useRef();
  const onChangeRef = useRef();
  const [defaultData, setDefaultData] = useState(null);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    const data = buildDefaultData(pages, sectionsSelection, linearCodes);
    setDefaultData(data);
  }, [pages, sectionsSelection]);

  // define external api
  compRef.current = {
    hasChanges: () => (!!Object.keys(changes).length),
    submit: () => setBarcodeInSection(collectCorrectFieldsOnSubmit({...defaultData, ...changes})),
    validate: () => (!formCompRef.current || formCompRef.current.validate())
  };

  onChangeRef.current = (data) => {
    const newData = mergeNewDataWithChanges(data, changes, defaultData);
    const newChanges = getOnlyDiffProps(defaultData, {...changes, ...newData}, true);
    setChanges(newChanges);
  };
  const cachedOnChange = useCallback((data) => onChangeRef.current(data), []);
  if (defaultData?.codeType && !defaultData?.codeGroup) {
    const isLinear = !!Object.values(codesByGroup.linearCodes).find(g => g.value === defaultData.codeType);
    const is2D = !!Object.values(codesByGroup.twoDCodes).find(g => g.value === defaultData.codeType);
    defaultData.codeGroup = isLinear? 'linearCodes' : is2D? 'twoDCodes' : null;
    if(!defaultData.codeGroup) console.error('Barcode type is not suppoted: ', defaultData.codeType);
  }

  const groupCodes = codesByGroup[changes.codeGroup || defaultData?.codeGroup];
  const codeSettings = groupCodes?.find(i => i.value === (changes.codeType || defaultData.codeType));

  return defaultData && (
    <InsertCodeForm
      codeGroups={codeGroups}
      groupCodes={groupCodes}
      codeSettings={codeSettings}
      {...restProps}
      {...defaultData}
      {...changes}
      compRef={formCompRef}
      onChange={cachedOnChange}
    />
  )
};

const mapStateToProps = state => ({
  sectionsSelection: state.core.sectionsSelection,
  pages: state.core.pages,
});

const mapDispatchToProps = dispatch => ({
  setBarcodeInSection: (data) => dispatch(setBarcodeInSection(data, true)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InsertCodeFormContainer);

const buildDefaultData = (pages, sectionsSelection) => {
  let selSec = null;
  for(let secId in sectionsSelection){
    const { section } = getAllLayers(secId, pages);
    selSec = section;
    break;
  }

  let res = {};
  if (!selSec?.code) {
    res['codeGroup'] = codeGroups[0];
    const codeSettings = codesByGroup[codeGroups[0]][0];
    res['codeType'] = codeSettings.value;
    const initOptions = collectFormOptionsFromCodeFormOptionsSettings(codeSettings.options);
    res = {...res, ...initOptions};
    res['text'] = '';
  }
  else {
    res = selSec.code;
  }

  return res
};

const mergeNewDataWithChanges = (newData, changes, defaultData) => {
  let res = {...newData};
  if (res.hasOwnProperty('codeGroup')) {
    const groupCodes = codesByGroup[res.codeGroup];
    if (groupCodes) {
      res['codeType'] = groupCodes[0].value;
    }
    else {
      delete res['codeGroup'];
    }
  }

  if (res.hasOwnProperty('codeType')) {
    const groupCodes = codesByGroup[res.codeGroup || changes.codeGroup || defaultData.codeGroup];
    const foundCode = groupCodes.find(i => i.value === res.codeType);
    if (foundCode) {
      const initOptions = collectFormOptionsFromCodeFormOptionsSettings(foundCode.options);
      for (const pName in initOptions) {
        if (changes.hasOwnProperty(pName)) {
          delete initOptions[pName];
        }
      }
      res = {...res, ...initOptions};
    }
    else {
      delete res['codeType'];
    }
  }

  return res;
};

const collectCorrectFieldsOnSubmit = (data) => {
  const groupCodes = codesByGroup[data.codeGroup];
  const foundCode = groupCodes.find(i => i.value === data.codeType);
  const initOptions = collectFormOptionsFromCodeFormOptionsSettings(foundCode.options);
  for (const pName in data) {
    if (
      pName !== 'codeGroup' && pName !== 'codeType' && pName !== 'text' &&
      !initOptions.hasOwnProperty(pName)
    ){
      delete data[pName];
    }
  }
  return data;
};

const collectFormOptionsFromCodeFormOptionsSettings = (codeOptionsSettings) => {
  const res = {};
  if (codeOptionsSettings) {
    for (let opType in codeOptionsSettings) {
      for (let fieldName in codeOptionsSettings[opType]) {
        const { defaultValueIndex, values } = codeOptionsSettings[opType][fieldName];
        res[fieldName] = values[defaultValueIndex].value;
      }
    }
  }

  return res;
};